import { Component, type ReactNode, type ErrorInfo } from 'react';
import { ThemeToggle } from '../../ui/atoms/ThemeToggle';
import { AquariumSwitcher } from '../../aquarium/AquariumSwitcher/AquariumSwitcher';
import { NewsButton } from '../../news/NewsButton';
import { AlertsHeaderButton } from '../../reefGuard/AlertsHeaderButton';
import './Header.css';

// The AquariumSwitcher reads from ActiveAquariumContext, which is only
// mounted for authenticated pages (see App.tsx). Header itself is also
// rendered on pages without that context (e.g. via page tests), so guard
// the same way ThemeToggleBoundary guards ThemeProvider below.
class AquariumSwitcherBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(_error: Error): { hasError: boolean } {
    return { hasError: true };
  }
  componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // Intentionally silent — missing ActiveAquariumProvider is expected in page tests
  }
  render(): ReactNode {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

class ThemeToggleBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(_error: Error): { hasError: boolean } {
    return { hasError: true };
  }
  componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // Intentionally silent — missing ThemeProvider is expected in page tests
  }
  render(): ReactNode {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

// The news and alerts icons need AuthProvider, a QueryClient and a router; page tests
// render the Header without them, so they disappear instead of crashing (as above).
class NewsButtonBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(_error: Error): { hasError: boolean } {
    return { hasError: true };
  }
  componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // Intentionally silent — missing AuthProvider / QueryClientProvider is expected in page tests
  }
  render(): ReactNode {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export function Header() {
  return (
    // The banner landmark is AppLayout's <header>; this is its content.
    <div className="header" data-testid="header">
      <div className="header__aquarium-switcher">
        <AquariumSwitcherBoundary>
          <AquariumSwitcher />
        </AquariumSwitcherBoundary>
      </div>
      <div className="header__actions">
        {/* Open notices where they are seen; the language moved to Settings only (usability audit §4.4). */}
        <NewsButtonBoundary>
          <AlertsHeaderButton />
        </NewsButtonBoundary>
        <NewsButtonBoundary>
          <NewsButton />
        </NewsButtonBoundary>
        <ThemeToggleBoundary>
          <ThemeToggle />
        </ThemeToggleBoundary>
      </div>
    </div>
  );
}
